import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Switch } from '../../ds';
import { WEB_ICO } from '../../shell/icons';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { useAsync } from '../../hooks/useAsync';
import { progressApi } from '../../api/progress';
import { screenShell } from '../lesson/common';

const MIN = 5;
const MAX = 100;
const STEP = 5;

const stepBtn = (disabled: boolean): CSSProperties => ({
  width: 44,
  height: 44,
  borderRadius: 999,
  border: '1px solid var(--border-default)',
  background: 'var(--surface-page)',
  color: 'var(--cta-default)',
  fontSize: 20,
  fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.35 : 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export function SrsSettingsScreen({
  onBack,
  onToast,
  onSrsEnabledSaved,
}: {
  onBack: () => void;
  onToast: (m: string) => void;
  /** Gọi sau khi công tắc "Dùng SRS" đã lưu xong — shell tải lại gamification để các trang khác đổi theo. */
  onSrsEnabledSaved: () => void;
}) {
  const gamification = useAsync(() => progressApi.getGamification(), []);
  // Giá trị user đang chỉnh; null = chưa chỉnh, hiển thị giá trị đã lưu từ server.
  const [draft, setDraft] = useState<number | null>(null);
  const value = draft ?? gamification.data?.srsSessionSize ?? null;
  const [enabledDraft, setEnabledDraft] = useState<boolean | null>(null);
  const srsEnabled = enabledDraft ?? gamification.data?.srsEnabled ?? true;

  const toggleSrs = (next: boolean) => {
    const previous = srsEnabled;
    setEnabledDraft(next);
    progressApi
      .setSrsEnabled(next)
      .then(onSrsEnabledSaved)
      .catch(() => {
        setEnabledDraft(previous);
        onToast('Không lưu được cài đặt. Thử lại nhé.');
      });
  };

  const change = (next: number) => {
    const clamped = Math.min(MAX, Math.max(MIN, next));
    const previous = value;
    setDraft(clamped);
    progressApi.updateSrsSettings(clamped).catch(() => {
      setDraft(previous);
      onToast('Không lưu được cài đặt. Thử lại nhé.');
    });
  };

  return (
    <div style={screenShell}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px 8px' }}>
        <button
          onClick={onBack}
          aria-label="Quay lại"
          style={{ width: 44, height: 44, marginLeft: -10, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {WEB_ICO.back('var(--ink)', 24)}
        </button>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>Cài đặt SRS</h1>
      </div>

      {gamification.loading && value === null ? (
        <LoadingState />
      ) : value === null ? (
        <ErrorState message="Không tải được cài đặt." />
      ) : (
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)', padding: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>Dùng SRS</div>
              <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                {srsEnabled
                  ? 'Chưa thuộc / Đã thuộc được lưu lại và tự lên lịch ôn (FSRS)'
                  : 'Đang tắt — flashcard chỉ duyệt tự do, không lên lịch ôn'}
              </div>
            </div>
            <Switch checked={srsEnabled} onChange={toggleSrs} />
          </div>

          <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>Số thẻ mỗi lần ôn</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
              <button onClick={() => change(value - STEP)} disabled={value <= MIN} style={stepBtn(value <= MIN)} aria-label="Giảm">
                −
              </button>
              <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--ink)', minWidth: 48, textAlign: 'center' }}>{value}</div>
              <button onClick={() => change(value + STEP)} disabled={value >= MAX} style={stepBtn(value >= MAX)} aria-label="Tăng">
                +
              </button>
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center' }}>Mỗi lần bấm “Bắt đầu ôn” chỉ đưa ra tối đa chừng này thẻ. Thẻ đến hạn còn lại để lần ôn sau.</div>
          </div>
        </div>
      )}
    </div>
  );
}
